import React, { useEffect, useRef } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import { hasCoachOversight } from '@rhwb/types';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useFilterState } from '../hooks/useFilterState';
import { useDashboardData } from '../hooks/useDashboardData';
import { useMenuHandlers } from '../hooks/useMenuHandlers';
import FilterChips from '../components/dashboard/FilterChips';
import DashboardWidgets from '../components/dashboard/DashboardWidgets';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import { useNavigate } from 'react-router-dom';
import { useOverridePath } from '../contexts/AppContext';
import { useNpsSurvey } from '../hooks/useNpsSurvey';
import { shouldPromptForSurvey } from '../lib/npsSurvey';

const showAnnouncements = process.env.REACT_APP_ENABLE_ANNOUNCEMENTS === 'true';

const DashboardContainer: React.FC = () => {
  const { user } = useAuth();
  const { effectiveEmail, isOverrideActive, overrideEmail } = useApp();
  
  // Use effectiveEmail (which respects override) for dashboard data
  const email = effectiveEmail || user?.email || '';

  // Don't default to 'runner' when user is loading - this prevents clearing coachList on refresh
  // Wait for user to load before determining role
  // Keep the actual user role even in override mode so admin filters remain visible
  const userRole = user?.role;

  // Debug logging for admin users (dev only — emails are PII)
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && user) {
      console.log('[DASHBOARD CONTAINER] User:', { email: user.email, role: user.role });
      console.log('[DASHBOARD CONTAINER] userRole will be:', userRole);
      console.log('[DASHBOARD CONTAINER] effectiveEmail:', effectiveEmail);
      console.log('[DASHBOARD CONTAINER] isOverrideActive:', isOverrideActive);
      if (isOverrideActive) {
        console.log('[DASHBOARD CONTAINER] Override mode - viewing as:', overrideEmail);
      }
    }
  }, [user, effectiveEmail, isOverrideActive, overrideEmail, userRole]);

  // Filter state
  const filterState = useFilterState(userRole);

  // Dashboard data
  const dashboardData = useDashboardData(
    email,
    userRole,
    filterState.season,
    filterState.selectedRunner,
    filterState.selectedCoach,
    filterState.hybridToggle,
    filterState.coachName,
    filterState.setCoachList,
    filterState.setRunnerList,
    filterState.setCoachName
  );

  // Menu handlers
  const menuHandlers = useMenuHandlers(
    filterState.setSeason,
    filterState.setSelectedCoach,
    filterState.setSelectedRunner,
    filterState.setHybridToggle,
    filterState.setSeasonMenuAnchor,
    filterState.setCoachMenuAnchor,
    filterState.setRunnerMenuAnchor,
    filterState.setHybridToggleMenuAnchor,
    filterState.setSearchQuery,
    filterState.setSearchResults,
    filterState.runnerList,
    dashboardData.searchRunners,
    email
  );

  // NPS survey: send a runner who has not answered to the survey page while
  // the admin-set window is open (Coach Portal -> Runner Survey). "Maybe
  // later" on that page dismisses it for the rest of the browser session.
  // Runners only — hybrids reach the survey by link.
  const navigate = useNavigate();
  const withOverride = useOverridePath();
  const npsSurvey = useNpsSurvey(user?.email || '', userRole);
  const promptForSurvey =
    npsSurvey.shouldShowSurvey &&
    shouldPromptForSurvey({ role: userRole, open: npsSurvey.open, alreadySubmitted: npsSurvey.alreadySubmitted });

  useEffect(() => {
    if (promptForSurvey) navigate(withOverride('/survey'));
  }, [promptForSurvey, navigate, withOverride]);

  // Ref for charts container to focus/scroll to when filter panel closes
  const chartsContainerRef = useRef<HTMLDivElement>(null);

  // Track previous selections to detect changes
  const prevSeasonRef = useRef<string>('');
  const isInitialMountRef = useRef(true);

  // After a runner picks a season, take them to their charts. (This used to
  // also close the filter panel, which no longer exists.)
  useEffect(() => {
    // Initialize refs on first run
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevSeasonRef.current = filterState.season;
      return;
    }

    if (userRole === 'runner') {
      const currentSeason = filterState.season;

      if (currentSeason && currentSeason !== prevSeasonRef.current) {
        setTimeout(() => {
          if (chartsContainerRef.current) {
            chartsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Also focus the container for keyboard navigation
            chartsContainerRef.current.focus();
          }
        }, 100);
      }

      prevSeasonRef.current = currentSeason;
    }
  }, [filterState.season, userRole]);

  // Format the last data refresh date
  const formatRefreshDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      // Handle ISO 8601 format (e.g., "2026-08-21T00:00:00Z") or simple date format (e.g., "2026-08-21")
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get the selected coach's name
  const getCoachName = () => {
    if (hasCoachOversight(userRole) && filterState.selectedCoach) {
      return filterState.selectedCoach;
    }
    if ((userRole === 'coach' || userRole === 'hybrid') && filterState.coachName) {
      return filterState.coachName;
    }
    return null;
  };

  return (
    <>
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 1 }}>
        {/* Hidden on the web while announcements and calendar events are tested
            through the mobile app with a limited set of testers. Set
            REACT_APP_ENABLE_ANNOUNCEMENTS=true once the mobile app is widely
            available. Even when enabled it renders nothing until announcements
            target runners — see the component comment. */}
        {showAnnouncements && <AnnouncementsBanner />}

        {/* Filter Bar — the chips are the controls: each opens its own menu
            in place. There is no separate filter panel to open first. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
            p: 1.5,
            bgcolor: 'rgba(24, 119, 242, 0.04)',
            borderRadius: 2,
            border: '1px solid rgba(24, 119, 242, 0.1)',
          }}
        >
          {getCoachName() && !hasCoachOversight(userRole) && (
            <Chip
              label={`Coach: ${getCoachName()}`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
          )}

          <FilterChips
            season={filterState.season}
            userRole={userRole}
            selectedCoach={filterState.selectedCoach}
            selectedRunner={filterState.selectedRunner}
            hybridToggle={filterState.hybridToggle}
            searchQuery={filterState.searchQuery}
            isOverrideActive={isOverrideActive}
            overrideEmail={overrideEmail}
            seasonOptions={filterState.seasonOptions}
            coachList={filterState.coachList}
            runnerList={filterState.runnerList}
            searchResults={filterState.searchResults}
            seasonMenuAnchor={filterState.seasonMenuAnchor}
            seasonMenuOpen={filterState.seasonMenuOpen}
            coachMenuAnchor={filterState.coachMenuAnchor}
            coachMenuOpen={filterState.coachMenuOpen}
            runnerMenuAnchor={filterState.runnerMenuAnchor}
            runnerMenuOpen={filterState.runnerMenuOpen}
            hybridToggleMenuAnchor={filterState.hybridToggleMenuAnchor}
            hybridToggleMenuOpen={filterState.hybridToggleMenuOpen}
            onSeasonMenuOpen={menuHandlers.handleSeasonMenuOpen}
            onSeasonMenuClose={menuHandlers.handleSeasonMenuClose}
            onSeasonChange={menuHandlers.handleSeasonChange}
            onCoachMenuOpen={menuHandlers.handleCoachMenuOpen}
            onCoachMenuClose={menuHandlers.handleCoachMenuClose}
            onCoachChangeFromChip={menuHandlers.handleCoachChangeFromChip}
            onRunnerMenuOpen={menuHandlers.handleRunnerMenuOpen}
            onRunnerMenuClose={menuHandlers.handleRunnerMenuClose}
            onRunnerChangeFromChip={menuHandlers.handleRunnerChangeFromChip}
            onHybridToggleMenuOpen={menuHandlers.handleHybridToggleMenuOpen}
            onHybridToggleMenuClose={menuHandlers.handleHybridToggleMenuClose}
            onHybridToggleChangeFromChip={menuHandlers.handleHybridToggleChangeFromChip}
            onSearchChange={menuHandlers.handleSearchChange}
            onRunnerSelect={menuHandlers.handleRunnerSelect}
          />

          {/* Data Refresh Date */}
          {dashboardData.lastDataRefresh && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              <UpdateIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Data as of {formatRefreshDate(dashboardData.lastDataRefresh)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Dashboard Widgets */}
        <Box ref={chartsContainerRef} tabIndex={-1} sx={{ outline: 'none' }}>
          <DashboardWidgets
            loading={dashboardData.loading}
            error={dashboardData.error}
            data={dashboardData.data}
            cumulativeScore={dashboardData.cumulativeScore}
            activitySummary={dashboardData.activitySummary}
            trainingFeedback={dashboardData.trainingFeedback}
            activityCalendar={dashboardData.activityCalendar}
            userEmail={email}
          />
        </Box>

        {/* Usage disclosure. Kept visible rather than buried in a policy page:
            the club records who signs in and how long they stay, and the people
            it records are volunteers. */}
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 3, textAlign: 'center', color: 'text.secondary' }}
        >
          We record basic app usage — sign-ins, time spent and pages visited — to
          improve these tools.
        </Typography>
      </Box>


    </>
  );
};

export default DashboardContainer;
